import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { AuthService } from '@service/auth.service';
import { UserCompanyService } from '@service/user-company.service';
import { VesselOwnershipSocketService } from '@service/vessel-ownership-socket.service';
import { VesselOwnershipService } from '@service/vessel-ownership.service';
import { ProjectStatus, Vessel } from 'icreate/common/models/vessel';
import { VesselOwnershipStatus, VesselOwnershipTransfer } from 'icreate/common/models/vessel-ownership-transfer';
import { merge, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-ownership-transfer-message',
    templateUrl: './ownership-transfer-message.component.html',
    styleUrls: ['./ownership-transfer-message.component.css'],
    standalone: false,
})
export class OwnershipTransferMessageComponent implements OnInit, OnChanges, OnDestroy {
    @Input() vessel: Vessel;
    @Input() editors: any[];
    @Input() ownershipTransfersList: VesselOwnershipTransfer[];
    @Output() onUpdateRequest: EventEmitter<any> = new EventEmitter();
    shouldShowMessage: boolean = false;
    messageType: OwnershipMessageType;
    ownershipTransfer: VesselOwnershipTransfer;
    private destroySubscriptions$: Subject<void> = new Subject();

    currentUser: { name: string; oid: string; email: string };
    assignerName: string;

    constructor(
        private vesselOwnershipService: VesselOwnershipService,
        private vesselOwnershipSocketService: VesselOwnershipSocketService,
        private authService: AuthService,
        private userCompanyService: UserCompanyService
    ) {
        this.currentUser = this.authService.getCurrentUser();
    }

    async ngOnInit() {
        this.startSubscriptions();
    }

    async ngOnChanges(changes: SimpleChanges) {
        if (changes.vessel) {
            this.messageType = null;
            this.shouldShowMessage = false;
            this.ownershipTransfer = null;

            if (!this.vessel) return;

            this.ownershipTransfersList = await this.vesselOwnershipService.getOwnershipTransfers(this.vessel.id);

            this.ownershipTransfersList?.forEach(transfer => {
                this.processOwnershipTransfer(transfer);
            });
        }
    }

    private startSubscriptions() {
        merge(
            this.vesselOwnershipSocketService.ownershipRequested$(),
            this.vesselOwnershipSocketService.ownershipClaimed$(),
            this.vesselOwnershipSocketService.ownershipRejected$(),
            this.vesselOwnershipSocketService.ownershipAssigned$(),
            this.vesselOwnershipSocketService.ownerLeft$(),
            this.vesselOwnershipSocketService.ownerRemoved$()
        )
            .pipe(takeUntil(this.destroySubscriptions$))
            .subscribe(async (transfer: VesselOwnershipTransfer) => {
                if (transfer && transfer.vesselId === this.vessel?.id) {
                    await this.processOwnershipTransfer(transfer);
                }
            });
    }

    ngOnDestroy(): void {
        this.destroySubscriptions$.next();
        this.destroySubscriptions$.complete();
    }

    private async processOwnershipTransfer(transfer: VesselOwnershipTransfer) {
        if (transfer.messageRead) return;

        if (transfer.ownershipStatus === VesselOwnershipStatus.Assigned && this.currentUser.oid === transfer.userOid) {
            const userInfo = await this.userCompanyService.getUserInfo(transfer.assignerOid);

            if (userInfo) {
                this.assignerName = userInfo.name;
            }
            await this.showCurrentMessage(transfer, OwnershipMessageType.OWNERSHIP_ASSIGNED_MESSAGE);
        } else if (
            transfer.ownershipStatus === VesselOwnershipStatus.Claimed &&
            this.currentUser.oid === transfer.userOid
        ) {
            await this.showCurrentMessage(transfer, OwnershipMessageType.OWNERSHIP_CLAIMED_MESSAGE);
        } else if (transfer.ownershipStatus === VesselOwnershipStatus.Requested && this.isOwnerVessel()) {
            await this.showCurrentMessage(transfer, OwnershipMessageType.OWNERSHIP_REQUESTED_MESSAGE);
        } else if (
            transfer.ownershipStatus === VesselOwnershipStatus.Left &&
            this.currentUser.oid !== transfer.userOid
        ) {
            await this.showCurrentMessage(transfer, OwnershipMessageType.OWNER_LEFT_MESSAGE);
        } else if (
            transfer.ownershipStatus === VesselOwnershipStatus.Rejected &&
            this.currentUser.oid === transfer.userOid
        ) {
            const userInfo = await this.userCompanyService.getUserInfo(transfer.assignerOid);

            if (userInfo) {
                this.assignerName = userInfo.name;
            }
            await this.showCurrentMessage(transfer, OwnershipMessageType.OWNERSHIP_REJECTED_MESSAGE);
        } else if (
            transfer.ownershipStatus === VesselOwnershipStatus.Removed &&
            this.currentUser.oid === transfer.userOid
        ) {
            await this.showCurrentMessage(transfer, OwnershipMessageType.OWNER_REMOVED_MESSAGE);
        }
    }

    async showCurrentMessage(transfer: VesselOwnershipTransfer, messageType: OwnershipMessageType) {
        this.ownershipTransfer = transfer;
        this.messageType = messageType;
        this.shouldShowMessage = true;
        if (
            this.messageType != OwnershipMessageType.OWNERSHIP_REQUESTED_MESSAGE &&
            this.messageType != OwnershipMessageType.OWNER_LEFT_MESSAGE
        )
            await this.markMessageAsRead(transfer.id);
    }

    private async markMessageAsRead(transferId: string) {
        try {
            if (transferId) {
                await this.vesselOwnershipService.readOwnershipMessage(transferId);
            }
        } catch (err) {
            console.error('Failed to mark message as read in DB:', err);
        }
    }

    showRequestedMessage(): boolean {
        return this.messageType === OwnershipMessageType.OWNERSHIP_REQUESTED_MESSAGE;
    }

    showClaimedMessage(): boolean {
        return this.messageType === OwnershipMessageType.OWNERSHIP_CLAIMED_MESSAGE;
    }

    showAssignedMessage(): boolean {
        return this.messageType === OwnershipMessageType.OWNERSHIP_ASSIGNED_MESSAGE;
    }

    showRejectedMessage(): boolean {
        return this.messageType === OwnershipMessageType.OWNERSHIP_REJECTED_MESSAGE;
    }

    showOwnerLeftMessage(): boolean {
        return this.messageType === OwnershipMessageType.OWNER_LEFT_MESSAGE;
    }

    showOwnerRemovedMessage(): boolean {
        return this.messageType === OwnershipMessageType.OWNER_REMOVED_MESSAGE;
    }

    isOwnerVessel(): boolean {
        return this.currentUser.oid === this.vessel.ownerOid;
    }

    async closeMessage() {
        try {
            if (this.ownershipTransfer) {
                await this.markMessageAsRead(this.ownershipTransfer.id);
            }
            this.messageType = null;
            this.shouldShowMessage = false;
            this.ownershipTransfer = null;
        } catch (err) {
            console.error(err);
        }
    }

    onApprove(): void {
        if (this.hasEditor()) return;
        this.updateRequest(VesselOwnershipStatus.Assigned);
    }

    onReject(): void {
        this.updateRequest(VesselOwnershipStatus.Rejected);
    }

    private async updateRequest(status: VesselOwnershipStatus): Promise<void> {
        this.onUpdateRequest.emit({
            selectedOwner: {
                name: this.ownershipTransfer?.userName ?? null,
                oid: this.ownershipTransfer?.userOid ?? null,
            },
            id: this.ownershipTransfer?.id ?? null,
            status: status,
        });

        await this.closeMessage();
    }

    hasEditor() {
        return this.editors?.length > 0;
    }

    isProjectPendingApproval() {
        return this.vessel.projectStatus === ProjectStatus.PendingApproval;
    }
}

export enum OwnershipMessageType {
    OWNERSHIP_CLAIMED_MESSAGE = 'ownership_claimed',
    OWNERSHIP_REQUESTED_MESSAGE = 'ownership_requested',
    OWNERSHIP_ASSIGNED_MESSAGE = 'ownership_assigned',
    OWNERSHIP_REJECTED_MESSAGE = 'ownership_rejected',
    OWNER_LEFT_MESSAGE = 'owner-left',
    OWNER_REMOVED_MESSAGE = 'owner-removed',
}
